import { useState, type FormEvent } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Loader2, MailCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { forgotPassword } from "@/lib/api";
import { createErrorMessage } from "@/lib/error-message";

export const Route = createFileRoute("/_auth/forgot-password")({
  component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await forgotPassword(email.trim());
      setDone(true);
    } catch (err) {
      setError(createErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">메일을 확인해주세요</CardTitle>
          <CardDescription>
            등록된 이메일이라면 비밀번호 재설정 링크를 보내드렸습니다. 30분 안에
            메일함을 확인해주세요.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex justify-center py-4">
          <MailCheck className="size-12 text-primary" />
        </CardContent>
        <CardFooter className="justify-center">
          <Link to="/login" className="text-sm font-medium text-primary underline-offset-4 hover:underline">
            로그인 페이지로 돌아가기
          </Link>
        </CardFooter>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl">비밀번호 찾기</CardTitle>
        <CardDescription>
          가입에 사용한 이메일을 입력하면 재설정 링크를 보내드립니다.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {error && (
            <div className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="email">이메일</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              disabled={submitting}
            />
          </div>
          <Button type="submit" size="lg" disabled={submitting || !email.trim()} className="mt-2">
            {submitting ? (
              <>
                <Loader2 className="mr-1 size-4 animate-spin" />
                전송 중...
              </>
            ) : (
              "재설정 링크 받기"
            )}
          </Button>
        </form>
      </CardContent>
      <CardFooter className="justify-center">
        <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground">
          로그인 페이지로 돌아가기
        </Link>
      </CardFooter>
    </Card>
  );
}
